import { Fragment, useEffect } from 'react';

import path from 'path';

import { Divider, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { Card, RouterLink, TwoLineGrid } from '~components';
import { pagePathSegments, queryParameters } from '~constants';
import { UserRoleEnum } from '~enums';
import { DeviceGroup, DeviceGroupService } from '~features/deviceGroups';
import DevicesService from '~features/devices/services/devicesService';
import OrganisationsService from '~features/organisations/services/organisationsService';
import { ResourceGroupService } from '~features/resourceGroups';
import useAuthorize from '~hooks/useAuthorize';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';
import { ifNullOrEmpty } from '~utils/stringUtils';

/**
 * Todo: This whole component needs loading and error states
 */
interface Props {
	deviceId: string;
	organisationId?: string | undefined;
}

const deviceService = getService(DevicesService);
const organisationsService = getService(OrganisationsService);
const deviceGroupService = getService(DeviceGroupService);
const resourceGroupService = getService(ResourceGroupService);

const DeviceInformationCard = ({ deviceId, organisationId }: Props) => {
	const { t } = useTranslation();
	const { palette } = useTheme();

	// Device data fetch
	const {
		data: deviceData,
		error: deviceError,
		isLoading: deviceIsLoading,
	} = useSWR(
		deviceService.getDeviceByIdCacheKey(deviceId, organisationId),
		() => deviceService.getDeviceById(deviceId, organisationId ?? undefined),
		{
			revalidateOnMount: false,
		}
	);

	const isDeviceLoading = deviceIsLoading && deviceData == undefined;
	const hasDeviceError = deviceError != undefined;

	const { authorize } = useAuthorize();
	const isSuperAdmin = authorize([UserRoleEnum.SuperAdmin]);

	// Organisation data fetch
	const effectiveOrganisationId = organisationId ?? deviceData?.organisationId?.toString();
	const organisationLink =
		effectiveOrganisationId ?
			path.join(
				'/',
				pagePathSegments.Organisations,
				effectiveOrganisationId,
				pagePathSegments.General
			)
		:	undefined;

	const {
		data: organisationData,
		error: organisationError,
		isLoading: organisationIsLoading,
		mutate: revalidateOrganisation,
	} = useSWR(
		organisationsService.getOrganisationByIdCacheKey(effectiveOrganisationId),
		([_, id]) => (id ? organisationsService.getOrganisationById(id) : null),
		{
			revalidateOnMount: false,
		}
	);

	const isOrganisationLoading = organisationIsLoading && organisationData == undefined;
	const hasOrganisationError = organisationError != undefined;

	const {
		data: deviceGroupData,
		error: deviceGroupError,
		isLoading: deviceGroupIsLoading,
	} = useSWR<DeviceGroup[]>(
		deviceGroupService.getDeviceGroupsByDeviceIdCacheKey(deviceId, effectiveOrganisationId),
		() => {
			if (!deviceId) {
				return [] as DeviceGroup[];
			}
			return deviceGroupService.getDeviceGroupsByDeviceId({
				deviceId: deviceId,
				overwrittenClientId: effectiveOrganisationId,
			});
		},
		{
			revalidateOnMount: true,
		}
	);

	const isDeviceGroupLoading = deviceGroupIsLoading && deviceGroupData == undefined;
	const hasDeviceGroupError = deviceGroupError != undefined;

	// Resource group data fetch
	const resourceGroupId = deviceData?.resourceGroupId?.toString();

	const {
		data: resourceGroupData,
		error: resourceGroupError,
		isLoading: resourceGroupIsLoading,
		mutate: revalidateResourceGroup,
	} = useSWR(
		resourceGroupService.getResourceGroupByIdCacheKey(resourceGroupId, organisationId),
		([_, id]) => (id ? resourceGroupService.getResourceGroupById(id, organisationId) : null),
		{
			revalidateOnMount: true,
		}
	);

	const isResourceGroupLoading = resourceGroupIsLoading && resourceGroupData == undefined;
	const hasResourceGroupError = resourceGroupError != undefined;

	// Organisation data revalidation
	useEffect(() => {
		if (effectiveOrganisationId) {
			revalidateOrganisation();
		}
	}, [effectiveOrganisationId, revalidateOrganisation]);

	// Resource group data revalidation
	useEffect(() => {
		if (resourceGroupId) {
			revalidateResourceGroup();
		}
	}, [resourceGroupId, revalidateResourceGroup]);

	// Field values
	const organisationName = organisationData?.name ?? t('unassigned', { ns: 'status' });

	const deviceGroupNames =
		deviceGroupData && deviceGroupData.length > 0 ?
			deviceGroupData.map((deviceGroup, index) => (
				<Fragment key={deviceGroup.id}>
					<RouterLink
						to={{
							pathname: path.join('/', pagePathSegments.DeviceGroups, deviceGroup.id),
							search:
								organisationId ? `${queryParameters.OverwrittenClientId}=${organisationId}` : '',
						}}
					>
						{deviceGroup.name}
					</RouterLink>
					{index == deviceGroupData.length - 1 ? '.' : ', '}
				</Fragment>
			))
		:	<Typography variant='label' color={palette.textPrimary.main}>
				{t('none', { ns: 'status' })}
			</Typography>;

	const resourceGroupName = resourceGroupData?.name ?? t('none', { ns: 'status' });

	const hasError =
		hasDeviceError || hasOrganisationError || hasDeviceGroupError || hasResourceGroupError;
	const isInitialLoading =
		isDeviceLoading || isOrganisationLoading || isDeviceGroupLoading || isResourceGroupLoading;

	return (
		<Card title={t('device.fields.generalInformation', { ns: 'entities' })} sx={{ height: '100%' }}>
			<TwoLineGrid
				isLoading={isInitialLoading}
				showError={hasError}
				twoLineItems={[
					{
						primary: t('general.name', { ns: 'entities' }),
						secondary: deviceData?.name,
					},
					{
						primary: t('device.fields.hardwareUid', { ns: 'entities' }),
						secondary: deviceData?.hardwareId,
					},
					{
						primary: t('device.fields.model', { ns: 'entities' }),
						secondary: deviceData?.model.name,
					},
					{
						primary: t('device.labels.firmwareVersion', { ns: 'entities' }),
						secondary: deviceData?.state?.firmware,
						// Needs to be here as some point, but data is not there
						// tooltipText={`Updated on `}
					},
					{
						primary: t('device.fields.bluetoothName', { ns: 'entities' }),
						secondary: deviceData?.bluetoothName,
					},
					{
						primary: t('device.fields.description', { ns: 'entities' }),
						secondary: ifNullOrEmpty(deviceData?.description, t('noDescription', { ns: 'status' })),
					},
				]}
			/>
			<Divider sx={{ borderColor: palette.borderTertiary.main, my: Dimens.spacingXl }} />

			<TwoLineGrid
				isLoading={isInitialLoading}
				showError={hasError}
				twoLineItems={[
					{
						primary: t('organisation.labels.single', { ns: 'entities' }),
						secondary: organisationName,
						link: organisationLink,
						isHidden: !isSuperAdmin,
					},
					{
						primary: t('resourceGroup.labels.single', { ns: 'entities' }),
						secondary: resourceGroupName,
					},
					{
						primary: t('deviceGroup.labels.plural', { ns: 'entities' }),
						secondary: deviceGroupNames,
					},
				]}
			/>
		</Card>
	);
};

export default DeviceInformationCard;
